import { useState } from 'react';
import { login, setTokens, ApiError } from '@/lib/api';

/**
 * Login screen for the admin panel. Same `/auth/login` endpoint as the
 * mobile app — but the JWT is then exchanged at `/admin/api/me`,
 * which 403s anyone without `role` in (`admin`, `super_admin`). If
 * that check fails we wipe tokens immediately and surface the error.
 */
export function AuthGate() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      const data = await login(email, password);
      setTokens(data.accessToken, data.refreshToken);

      // Verify role before letting them in. Hits the gated /me endpoint.
      const verify = await fetch('/admin/api/me', {
        headers: { Authorization: `Bearer ${data.accessToken}` },
      });
      if (verify.status === 403) {
        localStorage.clear();
        throw new Error('Your account does not have admin access.');
      }
      if (!verify.ok) {
        throw new Error('Verification failed (status ' + verify.status + ').');
      }
      window.dispatchEvent(new Event('taiya:auth-set'));
    } catch (e) {
      const m =
        e instanceof ApiError
          ? e.message
          : e instanceof Error
            ? e.message
            : 'Login failed.';
      setErr(m);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-bg">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div
            className="inline-block w-12 h-12 rounded-full mb-4"
            style={{ background: '#FFB400' }}
          />
          <h1 className="text-2xl font-bold tracking-tight">TAIYA Admin</h1>
          <p className="text-text2 text-sm mt-1">Sign in to continue</p>
        </div>

        <form onSubmit={onSubmit} className="ad-card p-6 space-y-4">
          <div>
            <label className="block text-xs uppercase tracking-wider text-text3 mb-1">
              Email
            </label>
            <input
              type="email"
              autoComplete="email"
              required
              autoFocus
              className="ad-input w-full"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-text3 mb-1">
              Password
            </label>
            <input
              type="password"
              autoComplete="current-password"
              required
              className="ad-input w-full"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          {err && (
            <div className="text-sm text-error bg-error/10 border border-error/30 rounded-lg p-3">
              {err}
            </div>
          )}
          <button
            type="submit"
            disabled={busy || !email || !password}
            className="ad-btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        <p className="text-center text-text3 text-xs mt-6">
          Admin-only. Regular accounts can't sign in here.
        </p>
      </div>
    </div>
  );
}
